import React, { useState, useEffect, useCallback, Fragment, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { dashboardToast } from "../../utils/dashboardToast";
import { Alert, Badge, Button, Input, Modal, Pagination, Select, Textarea } from "../../components/ui";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import ContentLoader from "../../components/ContentLoader";
import SaListPageShell from "../../components/sa/SaListPageShell";
import RecordDetailModal from "../../components/RecordDetailModal";
import {
  getFrameworks,
  getSourceTypes,
  addOrUpdateFramework,
  deleteFramework,
  downloadFramework,
  viewFramework,
  fetchFrameworkBlob,
  parseFrameworkFileNames,
  parseFrameworkUrls,
  displayFrameworkFileName,
} from "../../services/frameworkApi";
import {
  generateCapabilitiesFromFramework,
  createGeneratedCapabilities,
  buildGeneratedCapabilitySavePayload,
} from "../../services/capabilityGenerationApi";
import TableSortSelect from "../../components/TableSortSelect";
import { TABLE_SORT } from "../../utils/tableSort";
import { getBusinessTypeList } from "../../services/businessApi";
import { DEFAULT_ASSESSMENT_FORM_UI_CONFIG } from "../../utils/assessmentFormConfig";
import { parseMaturityOptionsJson } from "../../utils/subCapabilityAssessmentConfig";
import { QUESTION_STATE, withQuestionList } from "../../utils/subCapabilityQuestionUtils";
import SubCapabilityQuestionListEditor from "../../components/SubCapabilityQuestionListEditor";
import { Download, Eye, FileText, Layers, Link, Pencil, Plus, Search, Trash2, Wand2 } from "lucide-react";
import "../../assets/css/dashboard.css";
import "../../assets/css/record-detail-modal.css";
import { FRAMEWORK_STUDIO_LABELS } from "../../utils/frameworkStudioLabels";

const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".jpg", ".png", ".webp"];
const GENERATE_ESTIMATED_SECONDS = 120;

const getDefaultUserTypeId = (types) => {
  const itType = (types || []).find((bt) => String(bt.name || "").toLowerCase() === "it");
  return itType?.id != null ? String(itType.id) : "";
};

const hasSavedCapabilities = (framework) =>
  Number(framework?.capabilityCount ?? framework?.CapabilityCount ?? 0) > 0;

const formatGenerateCountdown = (totalSeconds) => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const GENERATE_STEPS = [
  "Analyze sources",
  "Generate capabilities",
  "Draft sub-capabilities",
];

const CapabilityGenerateLoader = ({ remainingSec, elapsedSec, sourceFrameworkName }) => {
  const isOvertime = remainingSec <= 0;
  const progress = Math.min(0.97, elapsedSec / GENERATE_ESTIMATED_SECONDS);
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress);
  const activeStep = isOvertime
    ? GENERATE_STEPS.length
    : remainingSec > 80
      ? 0
      : remainingSec > 40
        ? 1
        : 2;

  const hint = isOvertime
    ? "Taking a little longer than expected. Please keep waiting…"
    : remainingSec > 80
      ? "Reading uploaded sources…"
      : remainingSec > 40
        ? "AI is extracting capabilities…"
        : remainingSec > 12
          ? "Drafting sub-capabilities and assessment questions…"
          : "Preparing the review draft…";

  const timerLabel = isOvertime
    ? `+${formatGenerateCountdown(elapsedSec - GENERATE_ESTIMATED_SECONDS)}`
    : formatGenerateCountdown(remainingSec);

  return (
    <div className="fw-gen-loader" role="status" aria-live="polite" aria-busy="true">
      <div className="fw-gen-loader__ring" aria-hidden="true">
        <svg viewBox="0 0 120 120">
          <circle className="fw-gen-loader__track" cx="60" cy="60" r={radius} />
          <circle
            className="fw-gen-loader__progress"
            cx="60"
            cy="60"
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="fw-gen-loader__timer">
          <span className="fw-gen-loader__time">{timerLabel}</span>
          <span className="fw-gen-loader__time-label">
            {isOvertime ? "Still working" : "remaining"}
          </span>
        </div>
      </div>

      <div className="fw-gen-loader__copy">
        <p className="fw-gen-loader__title">Generating capabilities…</p>
        {sourceFrameworkName ? (
          <p className="fw-gen-loader__framework">{sourceFrameworkName}</p>
        ) : null}
        <p className="fw-gen-loader__hint">{hint}</p>
      </div>

      <ol className="fw-gen-loader__steps">
        {GENERATE_STEPS.map((label, index) => {
          const done = index < activeStep;
          const current = index === activeStep;
          return (
            <li
              key={label}
              className={[
                "fw-gen-loader__step",
                done ? "is-done" : "",
                current ? "is-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="fw-gen-loader__step-dot" aria-hidden="true" />
              {label}
            </li>
          );
        })}
      </ol>

      <p className="fw-gen-loader__warning">
        Expected about 2 minutes. Please don't leave this page, refresh, or log out until generation finishes.
      </p>
    </div>
  );
};

const isImageType = (contentType, fileName) => {
  if (contentType?.startsWith("image/")) return true;
  return /\.(jpg|jpeg|png|webp)$/i.test(fileName || "");
};

const isPdfType = (contentType, fileName) => {
  if (contentType?.includes("pdf")) return true;
  return /\.pdf$/i.test(fileName || "");
};

const AttachmentList = ({ items, canRemove, onRemove, onView, onDownload }) => {
  if (!items?.length) {
    return <div className="fw-attach-empty">No attachments yet.</div>;
  }

  return (
    <div className="fw-attach-list">
      {items.map((item) => (
        <div key={item.key} className="fw-attach-card">
          <div className="fw-attach-preview">
            {item.url && isImageType(item.contentType, item.name) ? (
              <img src={item.url} alt={displayFrameworkFileName(item.name)} />
            ) : item.url && isPdfType(item.contentType, item.name) ? (
              <iframe title={displayFrameworkFileName(item.name)} src={item.url} />
            ) : (
              <div className="fw-attach-empty">
                {displayFrameworkFileName(item.name) || "Attachment"}
                <div>Preview not available — use View or Download.</div>
              </div>
            )}
          </div>
          <div className="fw-attach-meta">
            <p className="fw-attach-name">{displayFrameworkFileName(item.name)}</p>
            <div className="fw-attach-actions">
              {onView && (
                <Button
                  variant="outline"
                  size="sm"
                  title="View"
                  onClick={() => onView(item)}
                >
                  <Eye />
                </Button>
              )}
              {onDownload && (
                <Button
                  variant="outline"
                  size="sm"
                  title="Download"
                  onClick={() => onDownload(item)}
                >
                  <Download />
                </Button>
              )}
              {canRemove && (
                <Button
                  variant="danger"
                  size="sm"
                  title="Remove"
                  onClick={() => onRemove(item)}
                >
                  <Trash2 />
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const emptyFrameworkForm = () => ({
  id: 0,
  sourceFrameworkName: "",
  referenceCompanyName: "",
  description: "",
  frameworkType: "",
  sourceTypeId: null,
  existingFiles: [],
  newFiles: [],
  urls: [""],
});

const isValidHttpUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return false;
  try {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const parsed = new URL(withScheme);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const formatFrameworkSources = (framework) => {
  const files = parseFrameworkFileNames(framework?.attachmentPath).length;
  const urls = parseFrameworkUrls(framework?.urls).length;
  const parts = [];
  if (files) parts.push(`${files} file${files > 1 ? "s" : ""}`);
  if (urls) parts.push(`${urls} URL${urls > 1 ? "s" : ""}`);
  return parts.length ? parts.join(" · ") : "";
};

/** Muted dash for missing table values */
const EmptyCell = () => <span className="app-td__empty">—</span>;

const UrlList = ({ urls, canEdit, onChange, onAdd, onRemove }) => {
  if (!urls?.length) {
    return <div className="fw-attach-empty">No URLs yet.</div>;
  }

  return (
    <div className="fw-url-list">
      {urls.map((url, index) => (
        <div key={`url-${index}`} className="fw-url-row">
          {canEdit ? (
            <Input
              type="text"
              inputMode="url"
              placeholder="https://example.com/framework"
              value={url}
              onChange={(e) => onChange(index, e.target.value)}
            />
          ) : url ? (
            <a
              className="fw-url-link"
              href={/^https?:\/\//i.test(url) ? url : `https://${url}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {url}
            </a>
          ) : (
            <span className="fw-url-link">—</span>
          )}
          {canEdit && (
            <Button
              variant="danger"
              size="sm"
              title="Remove URL"
              onClick={() => onRemove(index)}
              disabled={urls.length <= 1 && !String(url || "").trim()}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      ))}
      {canEdit && (
        <Button variant="outline" size="sm" className="fw-url-add" onClick={onAdd}>
          <Plus className="me-2" />
          Add URL
        </Button>
      )}
    </div>
  );
};

const CAPABILITIES_PATH = "/sa/capability_management";

const FrameworkManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [frameworkToDelete, setFrameworkToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [formData, setFormData] = useState(emptyFrameworkForm());
  const [sourceTypes, setSourceTypes] = useState([]);
  const [sourceTypesLoading, setSourceTypesLoading] = useState(false);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewPreviews, setViewPreviews] = useState([]);
  const [editPreviews, setEditPreviews] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showGeneratePrompt, setShowGeneratePrompt] = useState(false);
  const [pendingGenerateFramework, setPendingGenerateFramework] = useState(null);
  const [generateUserTypeId, setGenerateUserTypeId] = useState("");
  const [generatedDraft, setGeneratedDraft] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generatingSourceFrameworkName, setGeneratingSourceFrameworkName] = useState("");
  const [generateRemainingSec, setGenerateRemainingSec] = useState(GENERATE_ESTIMATED_SECONDS);
  const [generateElapsedSec, setGenerateElapsedSec] = useState(0);
  const [createGeneratedLoading, setCreateGeneratedLoading] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const fileInputRef = useRef(null);
  const searchReadyRef = useRef(false);
  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
  });

  const goToCapabilities = useCallback((frameworkId) => {
    const id = Number(frameworkId);
    navigate(
      id > 0
        ? `${CAPABILITIES_PATH}?frameworkId=${id}`
        : CAPABILITIES_PATH
    );
  }, [navigate]);

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const openGeneratePrompt = useCallback((framework) => {
    if (!framework?.id) return;

    const hasFile = parseFrameworkFileNames(framework.attachmentPath).length > 0;
    const hasUrl = parseFrameworkUrls(framework.urls).length > 0;
    if (!hasFile && !hasUrl) {
      dashboardToast.error("Add at least one attachment or URL");
      return;
    }

    setPendingGenerateFramework(framework);
    setGenerateUserTypeId(getDefaultUserTypeId(businessTypes));
    setShowGeneratePrompt(true);
  }, [businessTypes]);

  const cancelGeneratePrompt = useCallback(() => {
    setShowGeneratePrompt(false);
    setPendingGenerateFramework(null);
  }, []);

  const startGeneratedCapabilities = useCallback(async () => {
    const framework = pendingGenerateFramework;
    if (!framework?.id) return;

    const selectedTypeId = String(generateUserTypeId || "");
    if (!selectedTypeId) {
      dashboardToast.error("Please select a user type.");
      return;
    }

    const selected = businessTypes.find((bt) => String(bt.id) === selectedTypeId);
    setShowGeneratePrompt(false);

    try {
      setGenerateLoading(true);
      setGeneratingSourceFrameworkName(framework.sourceFrameworkName || "");
      const draft = await generateCapabilitiesFromFramework(framework.id);
      setGeneratedDraft({
        frameworkId: draft.frameworkId || framework.id,
        sourceFrameworkName:
          draft.sourceFrameworkName || framework.sourceFrameworkName || "",
        referenceCompanyName:
          draft.referenceCompanyName || framework.referenceCompanyName || "",
        businessTypeId: selectedTypeId,
        businessTypeName: selected?.name || "",
        capabilities: draft.capabilities || [],
      });
      setShowGenerateModal(true);
    } catch (err) {
      console.error("Error generating capabilities:", err);
      dashboardToast.error(err.message || "Failed to generate capabilities");
    } finally {
      setGenerateLoading(false);
      setGeneratingSourceFrameworkName("");
      setPendingGenerateFramework(null);
    }
  }, [pendingGenerateFramework, generateUserTypeId, businessTypes]);

  useEffect(() => {
    if (!generateLoading) {
      setGenerateRemainingSec(GENERATE_ESTIMATED_SECONDS);
      setGenerateElapsedSec(0);
      return undefined;
    }

    const startedAt = Date.now();
    setGenerateRemainingSec(GENERATE_ESTIMATED_SECONDS);
    setGenerateElapsedSec(0);

    const timerId = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setGenerateElapsedSec(elapsed);
      setGenerateRemainingSec(Math.max(0, GENERATE_ESTIMATED_SECONDS - elapsed));
    }, 250);

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.clearInterval(timerId);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [generateLoading]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getBusinessTypeList(1, 500);
        if (cancelled) return;
        setBusinessTypes((result.items || []).filter((bt) => bt.isActive !== false));
      } catch (err) {
        console.error("Failed to fetch business types:", err);
        if (!cancelled) setBusinessTypes([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showGeneratePrompt || generateUserTypeId) return;
    const defaultId = getDefaultUserTypeId(businessTypes);
    if (defaultId) setGenerateUserTypeId(defaultId);
  }, [showGeneratePrompt, generateUserTypeId, businessTypes]);

  const cancelGeneratedDraft = useCallback(() => {
    setShowGenerateModal(false);
    setGeneratedDraft(null);
  }, []);

  const updateDraftBusinessType = useCallback((businessTypeId) => {
    const selected = businessTypes.find((bt) => String(bt.id) === String(businessTypeId));
    setGeneratedDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        businessTypeId: businessTypeId ? String(businessTypeId) : "",
        businessTypeName: selected?.name || "",
      };
    });
  }, [businessTypes]);

  const updateDraftCapability = useCallback((capIndex, field, value) => {
    setGeneratedDraft((prev) => {
      if (!prev) return prev;
      const capabilities = [...(prev.capabilities || [])];
      if (!capabilities[capIndex]) return prev;
      capabilities[capIndex] = { ...capabilities[capIndex], [field]: value };
      return { ...prev, capabilities };
    });
  }, []);

  const updateDraftSubCapability = useCallback((capIndex, subIndex, field, value) => {
    setGeneratedDraft((prev) => {
      if (!prev) return prev;
      const capabilities = [...(prev.capabilities || [])];
      const capability = capabilities[capIndex];
      if (!capability) return prev;
      const subCapabilities = [...(capability.subCapabilities || [])];
      if (!subCapabilities[subIndex]) return prev;
      subCapabilities[subIndex] = { ...subCapabilities[subIndex], [field]: value };
      capabilities[capIndex] = { ...capability, subCapabilities };
      return { ...prev, capabilities };
    });
  }, []);

  const updateDraftQuestionList = useCallback((capIndex, subIndex, stateKind, nextList) => {
    setGeneratedDraft((prev) => {
      if (!prev) return prev;
      const capabilities = [...(prev.capabilities || [])];
      const capability = capabilities[capIndex];
      if (!capability) return prev;
      const subCapabilities = [...(capability.subCapabilities || [])];
      if (!subCapabilities[subIndex]) return prev;
      subCapabilities[subIndex] = withQuestionList(subCapabilities[subIndex], stateKind, nextList);
      capabilities[capIndex] = { ...capability, subCapabilities };
      return { ...prev, capabilities };
    });
  }, []);

  const updateDraftCapabilityQuestionList = useCallback((capIndex, stateKind, nextList) => {
    setGeneratedDraft((prev) => {
      if (!prev) return prev;
      const capabilities = [...(prev.capabilities || [])];
      if (!capabilities[capIndex]) return prev;
      capabilities[capIndex] = withQuestionList(capabilities[capIndex], stateKind, nextList);
      return { ...prev, capabilities };
    });
  }, []);

  const createGeneratedDraft = useCallback(async () => {
    if (!generatedDraft) return;

    const businessTypeId = Number(generatedDraft.businessTypeId);
    if (!businessTypeId) {
      dashboardToast.error("Select a user type before creating.");
      return;
    }

    const payload = buildGeneratedCapabilitySavePayload(generatedDraft, businessTypeId);
    if (!payload.length) {
      dashboardToast.error("Each capability needs a name.");
      return;
    }

    try {
      setCreateGeneratedLoading(true);
      await createGeneratedCapabilities(payload);
      const count = payload.length;
      dashboardToast.success(
        count === 1
          ? "Capability created successfully"
          : `${count} capabilities created successfully`
      );
      const frameworkId = generatedDraft.frameworkId;
      setShowGenerateModal(false);
      setGeneratedDraft(null);
      goToCapabilities(frameworkId);
    } catch (err) {
      console.error("Error creating generated capability:", err);
      dashboardToast.error(err.message || "Failed to create capability");
    } finally {
      setCreateGeneratedLoading(false);
    }
  }, [generatedDraft, goToCapabilities]);

  const fetchFrameworks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getFrameworks(
        apiPageInfo.pageNumber,
        apiPageInfo.pageSize,
        sortKey,
        appliedSearch
      );

      setFrameworks(response.items || []);
      const totalPages = Math.max(
        1,
        Math.ceil((response.totalCount || 0) / (response.pageSize || apiPageInfo.pageSize))
      );
      setApiPageInfo((prev) => ({
        ...prev,
        totalPages,
        totalCount: response.totalCount || 0,
      }));
    } catch (err) {
      console.error("Error fetching frameworks:", err);
      setError("Failed to fetch sources");
      dashboardToast.error("Failed to fetch sources");
    } finally {
      setLoading(false);
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.pageSize, sortKey, appliedSearch]);

  const loadSourceTypes = useCallback(async () => {
    try {
      setSourceTypesLoading(true);
      const items = await getSourceTypes();
      setSourceTypes(items);
      return items;
    } catch (err) {
      console.error("Error loading source types:", err);
      dashboardToast.error(err.message || "Failed to load source types");
      return [];
    } finally {
      setSourceTypesLoading(false);
    }
  }, []);

  const handleShow = useCallback(async (framework = null) => {
    const types = await loadSourceTypes();

    if (framework) {
      const existingUrls = parseFrameworkUrls(framework.urls);
      const rawTypeId = framework.sourceTypeId ?? framework.SourceTypeId;
      const parsedTypeId = rawTypeId != null && Number(rawTypeId) > 0 ? Number(rawTypeId) : null;
      const frameworkType = framework.frameworkType || "";
      const matchedByName = types.find(
        (type) => type.name.toLowerCase() === frameworkType.trim().toLowerCase()
      );
      const sourceTypeId = parsedTypeId ?? matchedByName?.id ?? null;

      setFormData({
        id: framework.id,
        sourceFrameworkName: framework.sourceFrameworkName || "",
        referenceCompanyName: framework.referenceCompanyName || "",
        description: framework.description || "",
        frameworkType: sourceTypeId
          ? types.find((type) => type.id === sourceTypeId)?.name ?? frameworkType
          : frameworkType,
        sourceTypeId,
        existingFiles: parseFrameworkFileNames(framework.attachmentPath),
        newFiles: [],
        urls: existingUrls.length ? existingUrls : [""],
      });
    } else {
      setFormData(emptyFrameworkForm());
    }
    setShowModal(true);
  }, [loadSourceTypes]);

  const handleClose = useCallback(() => {
    setShowModal(false);
    setFormData(emptyFrameworkForm());
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleFileChange = useCallback((e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const valid = [];
    for (const file of selected) {
      const ext = `.${file.name.split(".").pop()?.toLowerCase() || ""}`;
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        dashboardToast.error(`Invalid file type for ${file.name}. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`);
        continue;
      }
      valid.push(file);
    }

    if (valid.length) {
      setFormData((prev) => ({
        ...prev,
        newFiles: [...prev.newFiles, ...valid],
      }));
    }

    e.target.value = "";
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const sourceFrameworkName = formData.sourceFrameworkName?.trim();
      if (!sourceFrameworkName) {
        dashboardToast.error(`${FRAMEWORK_STUDIO_LABELS.sourceName} is required`);
        return;
      }

      const sourceTypeId = formData.sourceTypeId;
      if (!sourceTypeId) {
        dashboardToast.error(`${FRAMEWORK_STUDIO_LABELS.sourceType} is required`);
        return;
      }

      const selected = sourceTypes.find((type) => type.id === sourceTypeId);
      const frameworkType = selected?.name ?? formData.frameworkType?.trim() ?? "";
      if (!frameworkType) {
        dashboardToast.error(`${FRAMEWORK_STUDIO_LABELS.sourceType} is required`);
        return;
      }

      const urls = (formData.urls || []).map((url) => url.trim()).filter(Boolean);
      const invalidUrl = urls.find((url) => !isValidHttpUrl(url));
      if (invalidUrl) {
        dashboardToast.error("Enter valid http or https URLs");
        return;
      }

      const hasFile = formData.existingFiles.length > 0 || formData.newFiles.length > 0;
      if (!hasFile && urls.length === 0) {
        dashboardToast.error("Add at least one attachment or URL");
        return;
      }

      try {
        setLoading(true);
        await addOrUpdateFramework({
          id: formData.id,
          sourceFrameworkName,
          referenceCompanyName: formData.referenceCompanyName,
          description: formData.description,
          frameworkType,
          sourceTypeId,
          urls,
          keepFiles: formData.existingFiles,
          files: formData.newFiles,
        });

        dashboardToast.success(
          formData.id === 0
            ? "Source created successfully"
            : "Source updated successfully"
        );
        handleClose();
        await fetchFrameworks();
      } catch (err) {
        console.error("Error saving framework:", err);
        dashboardToast.error(err.message || "Failed to save source");
      } finally {
        setLoading(false);
      }
    },
    [formData, handleClose, fetchFrameworks, sourceTypes]
  );

  const sourceTypeSelectOptions = useMemo(
    () => sourceTypes.map((type) => ({ value: String(type.id), label: type.name })),
    [sourceTypes]
  );

  const handleDelete = useCallback((framework) => {
    setFrameworkToDelete(framework);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!frameworkToDelete) return;

    try {
      setLoading(true);
      await deleteFramework(frameworkToDelete.id);
      dashboardToast.success("Source deleted successfully");
      setShowDeleteModal(false);
      setFrameworkToDelete(null);
      await fetchFrameworks();
    } catch (err) {
      console.error("Error deleting framework:", err);
      dashboardToast.error(err.message || "Failed to delete source");
    } finally {
      setLoading(false);
    }
  }, [frameworkToDelete, fetchFrameworks]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setFrameworkToDelete(null);
  }, []);

  const openViewModal = useCallback((framework) => {
    setViewRecord(framework);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
    setViewPreviews([]);
  }, []);

  const goToPage = useCallback(
    (n) => {
      const pg = Math.min(Math.max(n, 1), apiPageInfo.totalPages);
      if (pg !== apiPageInfo.pageNumber) {
        setApiPageInfo((prev) => ({ ...prev, pageNumber: pg }));
      }
    },
    [apiPageInfo.pageNumber, apiPageInfo.totalPages]
  );

  const handleSortChange = (nextSort) => {
    setSortKey(nextSort);
    setApiPageInfo((prev) =>
      prev.pageNumber === 1 ? prev : { ...prev, pageNumber: 1 }
    );
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  useEffect(() => {
    fetchFrameworks();
  }, [fetchFrameworks]);

  useEffect(() => {
    if (!searchReadyRef.current) {
      searchReadyRef.current = true;
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setAppliedSearch(searchTerm);
      setApiPageInfo((prev) =>
        prev.pageNumber === 1 ? prev : { ...prev, pageNumber: 1 }
      );
    }, 300);

    return () => window.clearTimeout(timerId);
  }, [searchTerm]);

  useEffect(() => {
    if (!showViewModal || !viewRecord?.id) {
      setViewPreviews([]);
      return;
    }

    const names = parseFrameworkFileNames(viewRecord.attachmentPath);
    let urls = [];
    let cancelled = false;

    (async () => {
      const next = [];
      for (const name of names) {
        try {
          const { blob, contentType } = await fetchFrameworkBlob(viewRecord.id, name);
          const url = URL.createObjectURL(blob);
          urls.push(url);
          next.push({ key: name, name, url, contentType });
        } catch {
          next.push({ key: name, name, url: null, contentType: "" });
        }
      }
      if (!cancelled) setViewPreviews(next);
    })();

    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [showViewModal, viewRecord]);

  useEffect(() => {
    if (!showModal || !formData.id || formData.existingFiles.length === 0) {
      setEditPreviews([]);
      return;
    }

    let urls = [];
    let cancelled = false;

    (async () => {
      const next = [];
      for (const name of formData.existingFiles) {
        try {
          const { blob, contentType } = await fetchFrameworkBlob(formData.id, name);
          const url = URL.createObjectURL(blob);
          urls.push(url);
          next.push({ key: name, name, url, contentType, stored: true });
        } catch {
          next.push({ key: name, name, url: null, contentType: "", stored: true });
        }
      }
      if (!cancelled) setEditPreviews(next);
    })();

    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [showModal, formData.id, formData.existingFiles]);

  const newFilePreviews = useMemo(
    () =>
      formData.newFiles.map((file, index) => ({
        key: `new-${file.name}-${file.lastModified}-${index}`,
        name: file.name,
        url: URL.createObjectURL(file),
        contentType: file.type,
        stored: false,
        file,
        index,
      })),
    [formData.newFiles]
  );

  useEffect(() => {
    return () => newFilePreviews.forEach((item) => URL.revokeObjectURL(item.url));
  }, [newFilePreviews]);

  const editAttachmentItems = [...editPreviews, ...newFilePreviews];

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={<Menu onSelect={handleMenuSelection} selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />}
    >
      <SaListPageShell topBarTitle={FRAMEWORK_STUDIO_LABELS.sourceDocuments}>
            <div className="dash-frame-parent1">
              <div className="dash-list-toolbar">
                <div className="dash-list-toolbar__intro">
                  <h5 className="dash-list-toolbar__title">
                    {FRAMEWORK_STUDIO_LABELS.sourceDocumentsManageTitle}
                  </h5>
                  <p className="dash-list-toolbar__desc">
                    {FRAMEWORK_STUDIO_LABELS.sourceDocumentsManageDescription}
                  </p>
                </div>
                <div className="dash-list-toolbar__actions">
                  <div className="dash-search-field dash-table-search">
                    <Search className="dash-search-field__icon" aria-hidden />
                    <input
                      type="text"
                      placeholder="Search name, type, company…"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="dash-search-field__input"
                      disabled={loading}
                    />
                  </div>
                  <TableSortSelect variant="chip"
                    value={sortKey}
                    onChange={handleSortChange}
                    disabled={loading}
                  />
                  <Button variant="primary" className="sa-create-btn" onClick={() => handleShow()} disabled={loading}>
                    {FRAMEWORK_STUDIO_LABELS.sourceDocumentsCreate}
                  </Button>
                </div>
              </div>

              {loading ? (
                <ContentLoader label="Loading…" />
              ) : error ? (
                <Alert variant="error">{error}</Alert>
              ) : (
                <Fragment>
                  <div className="app-table-wrap app-table-wrap--refined">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>{FRAMEWORK_STUDIO_LABELS.sourceName}</th>
                          <th>Company</th>
                          <th>Sources</th>
                          <th>Created At</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {frameworks.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center">
                              <span className="app-td__desc">
                                {FRAMEWORK_STUDIO_LABELS.sourceEmptyList}
                              </span>
                            </td>
                          </tr>
                        ) : (
                          frameworks.map((framework, index) => {
                            const rowNumber =
                              (apiPageInfo.pageNumber - 1) * apiPageInfo.pageSize + index + 1;
                            const companyName = framework.referenceCompanyName || "";
                            const sourcesLabel = formatFrameworkSources(framework);
                            const createdLabel = framework.createdAt
                              ? new Date(framework.createdAt).toLocaleDateString()
                              : "";

                            return (
                              <tr
                                key={framework.id}
                                className="rdm-row-clickable"
                                onClick={() => openViewModal(framework)}
                              >
                                <td>
                                  <span className="app-td__index">{rowNumber}</span>
                                </td>
                                <td>
                                  <span className="app-td__title">
                                    {framework.sourceFrameworkName}
                                  </span>
                                </td>
                                <td>
                                  {companyName ? (
                                    <span className="app-td__desc" title={companyName}>
                                      {companyName}
                                    </span>
                                  ) : (
                                    <EmptyCell />
                                  )}
                                </td>
                                <td>
                                  {sourcesLabel ? (
                                    <span className="app-td__meta">{sourcesLabel}</span>
                                  ) : (
                                    <EmptyCell />
                                  )}
                                </td>
                                <td>
                                  {createdLabel ? (
                                    <span className="app-td__meta">{createdLabel}</span>
                                  ) : (
                                    <EmptyCell />
                                  )}
                                </td>
                                <td
                                  className="dash-mobile-table-actions"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="btn-list-actions">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="btn-list-edit"
                                      onClick={() => handleShow(framework)}
                                      title="Edit"
                                      aria-label={`Edit ${framework.sourceFrameworkName}`}
                                    >
                                      <Pencil />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="btn-list-edit"
                                      onClick={() => openGeneratePrompt(framework)}
                                      disabled={generateLoading || createGeneratedLoading}
                                      title="Generate Capabilities and Sub Capabilities"
                                      aria-label={`Generate capabilities for ${framework.sourceFrameworkName}`}
                                    >
                                      <Wand2 />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="btn-list-edit"
                                      onClick={() => goToCapabilities(framework.id)}
                                      disabled={!hasSavedCapabilities(framework)}
                                      title={
                                        hasSavedCapabilities(framework)
                                          ? "View capabilities"
                                          : "Generate and save capabilities first"
                                      }
                                      aria-label={`View capabilities for ${framework.sourceFrameworkName}`}
                                    >
                                      <Layers />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      className="btn-list-delete"
                                      onClick={() => handleDelete(framework)}
                                      disabled={loading}
                                      title="Delete"
                                      aria-label={`Delete ${framework.sourceFrameworkName}`}
                                    >
                                      <Trash2 />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={apiPageInfo.pageNumber}
                    totalPages={apiPageInfo.totalPages}
                    onPageChange={goToPage}
                    className="justify-center mt-4"
                  />
                </Fragment>
              )}
            </div>
      </SaListPageShell>

      <Modal
        open={showModal}
        onClose={handleClose}
        size="lg"
        className="fw-framework-form-modal"
        title={
          <span className="inline-flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {formData.id
              ? FRAMEWORK_STUDIO_LABELS.sourceDocumentsEdit
              : FRAMEWORK_STUDIO_LABELS.sourceDocumentsCreate}
          </span>
        }
        footer={
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="framework-form"
              variant="primary"
              isLoading={loading}
              disabled={loading}
            >
              {loading ? "Saving..." : FRAMEWORK_STUDIO_LABELS.sourceDocumentsSave}
            </Button>
          </>
        }
      >
        <form id="framework-form" onSubmit={handleSubmit} className="fw-framework-form-body">
          <Input
            label={FRAMEWORK_STUDIO_LABELS.sourceName}
            name="sourceFrameworkName"
            value={formData.sourceFrameworkName}
            onChange={handleChange}
            required
            placeholder={FRAMEWORK_STUDIO_LABELS.sourceNamePlaceholder}
          />
          <Input
            label="Reference Company Name"
            name="referenceCompanyName"
            value={formData.referenceCompanyName}
            onChange={handleChange}
            placeholder="Enter reference company name"
          />
          <Textarea
            label="Description"
            name="description"
            rows={3}
            value={formData.description}
            onChange={handleChange}
            placeholder={FRAMEWORK_STUDIO_LABELS.sourceDescriptionPlaceholder}
          />
          <Select
            label={FRAMEWORK_STUDIO_LABELS.sourceType}
            searchable
            required
            disabled={sourceTypesLoading}
            placeholder={
              sourceTypesLoading
                ? "Loading…"
                : FRAMEWORK_STUDIO_LABELS.sourceTypeSelectPlaceholder
            }
            value={formData.sourceTypeId != null ? String(formData.sourceTypeId) : ""}
            options={sourceTypeSelectOptions}
            onChange={(value) => {
              const selected = sourceTypes.find((type) => String(type.id) === value);
              setFormData((prev) => ({
                ...prev,
                sourceTypeId: selected?.id ?? null,
                frameworkType: selected?.name ?? "",
              }));
            }}
          />
          <div>
            <p className="mb-2 flex items-center gap-2 text-sm font-medium text-[var(--app-text)]">
              <FileText className="h-4 w-4" />
              Attachments
            </p>
            <AttachmentList
              items={editAttachmentItems}
              canRemove
              onRemove={(item) => {
                if (item.stored) {
                  setFormData((prev) => ({
                    ...prev,
                    existingFiles: prev.existingFiles.filter((name) => name !== item.name),
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    newFiles: prev.newFiles.filter((_, idx) => idx !== item.index),
                  }));
                }
              }}
              onView={async (item) => {
                try {
                  if (item.stored) {
                    await viewFramework(formData.id, item.name);
                  } else if (item.url) {
                    window.open(item.url, "_blank", "noopener,noreferrer");
                  }
                } catch (err) {
                  dashboardToast.error(err.message || "Failed to open file");
                }
              }}
              onDownload={async (item) => {
                try {
                  if (item.stored) {
                    await downloadFramework(formData.id, item.name);
                  } else if (item.file) {
                    const url = URL.createObjectURL(item.file);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = item.file.name;
                    link.click();
                    URL.revokeObjectURL(url);
                  }
                } catch (err) {
                  dashboardToast.error(err.message || "Failed to download file");
                }
              }}
            />
            <input
              ref={fileInputRef}
              className="mt-3 fw-file-input"
              type="file"
              multiple
              accept=".pdf,.doc,.jpg,.png,.webp"
              onChange={handleFileChange}
            />
            <p className="mt-1 text-xs text-[var(--app-text-muted)]">
              Allowed: .pdf, .doc, .jpg, .png, .webp. You can add multiple files.
            </p>
          </div>
          <div>
            <p className="mb-2 flex items-center gap-2 text-sm font-medium text-[var(--app-text)]">
              <Link className="h-4 w-4" />
              URLs
            </p>
            <UrlList
              urls={formData.urls}
              canEdit
              onChange={(index, value) => {
                setFormData((prev) => {
                  const next = [...(prev.urls || [])];
                  next[index] = value;
                  return { ...prev, urls: next };
                });
              }}
              onAdd={() => {
                setFormData((prev) => ({
                  ...prev,
                  urls: [...(prev.urls || []), ""],
                }));
              }}
              onRemove={(index) => {
                setFormData((prev) => {
                  const next = (prev.urls || []).filter((_, idx) => idx !== index);
                  return { ...prev, urls: next.length ? next : [""] };
                });
              }}
            />
            <p className="mt-1 text-xs text-[var(--app-text-muted)]">
              Provide at least one attachment, at least one URL, or both.
            </p>
          </div>
        </form>
      </Modal>

      <Modal
        open={showDeleteModal}
        onClose={cancelDelete}
        title="Confirm Delete"
        footer={
          <>
            <Button variant="outline" onClick={cancelDelete} disabled={loading}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete} isLoading={loading} disabled={loading}>
              {loading ? "Deleting..." : "Delete"}
            </Button>
          </>
        }
      >
        <p className="m-0 text-[var(--app-text)]">
          Are you sure you want to delete{" "}
          <strong>{frameworkToDelete?.sourceFrameworkName}</strong>?
        </p>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        footerLayout="two-row"
        title={viewRecord?.sourceFrameworkName || FRAMEWORK_STUDIO_LABELS.sourceDetails}
        subtitle={FRAMEWORK_STUDIO_LABELS.sourceRecord}
        fields={[
          { label: FRAMEWORK_STUDIO_LABELS.sourceName, value: viewRecord?.sourceFrameworkName },
          { label: "Reference Company", value: viewRecord?.referenceCompanyName },
          {
            label: "Description",
            value: viewRecord?.description,
            fullWidth: true,
          },
          {
            label: FRAMEWORK_STUDIO_LABELS.sourceType,
            value: viewRecord?.frameworkType,
            fullWidth: true,
          },
          {
            label: "Created At",
            value: viewRecord?.createdAt
              ? new Date(viewRecord.createdAt).toLocaleDateString()
              : undefined,
          },
          {
            label: "Updated At",
            value: viewRecord?.updatedAt
              ? new Date(viewRecord.updatedAt).toLocaleDateString()
              : undefined,
          },
        ]}
        actions={[
          {
            type: "custom",
            onClick: () => {},
            render: (
              <Button
                variant="outline"
                className="rdm-footer-btn"
                disabled={!hasSavedCapabilities(viewRecord)}
                title={
                  hasSavedCapabilities(viewRecord)
                    ? "View capabilities"
                    : "Generate and save capabilities first"
                }
                onClick={() => {
                  const framework = viewRecord;
                  closeViewModal();
                  if (framework) goToCapabilities(framework.id);
                }}
              >
                <Layers size={16} aria-hidden />
                View Capabilities
              </Button>
            ),
          },
          {
            type: "custom",
            onClick: () => {},
            wide: true,
            render: (
              <Button
                variant="outline"
                className="rdm-footer-btn rdm-footer-btn--wide"
                disabled={generateLoading || createGeneratedLoading}
                title="Generate capabilities and sub-capabilities from this source"
                onClick={() => {
                  const framework = viewRecord;
                  closeViewModal();
                  if (framework) openGeneratePrompt(framework);
                }}
              >
                <Wand2 size={16} aria-hidden />
                Generate capabilities
              </Button>
            ),
          },
          {
            type: "edit",
            onClick: () => {
              const framework = viewRecord;
              closeViewModal();
              if (framework) handleShow(framework);
            },
          },
          {
            type: "delete",
            onClick: () => {
              const framework = viewRecord;
              closeViewModal();
              if (framework) handleDelete(framework);
            },
          },
        ]}
      >
        <div className="rdm-group">
          <h6 className="rdm-group-title">Attachments</h6>
          <AttachmentList
            items={viewPreviews}
            onView={async (item) => {
              try {
                await viewFramework(viewRecord.id, item.name);
              } catch (err) {
                dashboardToast.error(err.message || "Failed to open file");
              }
            }}
            onDownload={async (item) => {
              try {
                await downloadFramework(viewRecord.id, item.name);
              } catch (err) {
                dashboardToast.error(err.message || "Failed to download file");
              }
            }}
          />
        </div>
        <div className="rdm-group">
          <h6 className="rdm-group-title">URLs</h6>
          <UrlList urls={parseFrameworkUrls(viewRecord?.urls)} />
        </div>
      </RecordDetailModal>

      <Modal
        open={showGeneratePrompt}
        onClose={cancelGeneratePrompt}
        title={
          <span className="inline-flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Generate Capabilities
          </span>
        }
        footer={
          <>
            <Button variant="outline" onClick={cancelGeneratePrompt}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={startGeneratedCapabilities}
              disabled={!generateUserTypeId}
            >
              <Wand2 className="me-2" />
              Generate
            </Button>
          </>
        }
      >
        <p className="mb-4 text-[var(--app-text)]">
          Which user type should the generated capabilities use
          {pendingGenerateFramework?.sourceFrameworkName ? (
            <>
              {" "}
              for <strong>{pendingGenerateFramework.sourceFrameworkName}</strong>
            </>
          ) : null}
          ?
        </p>
        <Select
          label="User Type"
          required
          value={generateUserTypeId}
          onChange={setGenerateUserTypeId}
          placeholder="Select user type"
          options={businessTypes.map((bt) => ({
            value: String(bt.id),
            label: bt.name,
          }))}
        />
        <p className="mt-2 text-xs text-[var(--app-text-muted)]">
          IT is selected by default. You can change this again after generation.
        </p>
      </Modal>

      {generateLoading
        ? createPortal(
            <div
              className="app-modal-root app-modal-root--elevated"
              role="dialog"
              aria-modal="true"
              aria-busy="true"
            >
              <div className="app-modal-backdrop" aria-hidden="true" />
              <div className="app-modal-surface app-modal-surface--md fw-gen-modal">
                <div className="fw-gen-modal-body">
                  <CapabilityGenerateLoader
                    remainingSec={generateRemainingSec}
                    elapsedSec={generateElapsedSec}
                    sourceFrameworkName={generatingSourceFrameworkName}
                  />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <Modal
        open={showGenerateModal}
        onClose={cancelGeneratedDraft}
        size="xl"
        className="record-detail-modal-content"
        title="Generated Capabilities"
        footer={
          <>
            <Button
              variant="outline"
              className="rdm-footer-btn"
              onClick={cancelGeneratedDraft}
              disabled={createGeneratedLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="rdm-footer-btn"
              onClick={createGeneratedDraft}
              isLoading={createGeneratedLoading}
              disabled={
                createGeneratedLoading ||
                !generatedDraft ||
                !generatedDraft.businessTypeId
              }
            >
              {createGeneratedLoading ? "Creating..." : "Create"}
            </Button>
          </>
        }
      >
        <p className="rdm-subtitle mb-4">
          Edit extracted capabilities. You can still change the user type before creating.
        </p>
        <div className="rdm-body">
          {generatedDraft ? (
            <>
              <div className="rdm-field-grid">
                <div className="rdm-field">
                  <div className="rdm-field-label">{FRAMEWORK_STUDIO_LABELS.sourceName}</div>
                  <div className="rdm-field-value">{generatedDraft.sourceFrameworkName}</div>
                </div>
                {generatedDraft.referenceCompanyName ? (
                  <div className="rdm-field">
                    <div className="rdm-field-label">Reference Company</div>
                    <div className="rdm-field-value">{generatedDraft.referenceCompanyName}</div>
                  </div>
                ) : null}
                <div className="rdm-field">
                  <Select
                    label="User Type"
                    required
                    value={String(generatedDraft.businessTypeId || "")}
                    onChange={updateDraftBusinessType}
                    disabled={createGeneratedLoading}
                    placeholder="Select user type"
                    options={businessTypes.map((bt) => ({
                      value: String(bt.id),
                      label: bt.name,
                    }))}
                  />
                </div>
                <div className="rdm-field">
                  <div className="rdm-field-label">Capabilities</div>
                  <div className="rdm-field-value">
                    {generatedDraft.capabilities?.length || 0}
                  </div>
                </div>
              </div>

              {(generatedDraft.capabilities || []).map((capability, capIndex) => (
                <div key={capability.id || capIndex} className="rdm-group">
                  <h6 className="rdm-group-title d-flex align-items-center gap-2 flex-wrap">
                    <span className="rdm-subcap-index">{capIndex + 1}</span>
                    <Input
                      className="rdm-edit-title h-8 text-sm"
                      value={capability.capabilityName || ""}
                      onChange={(e) =>
                        updateDraftCapability(capIndex, "capabilityName", e.target.value)
                      }
                      disabled={createGeneratedLoading}
                      placeholder={`Capability ${capIndex + 1}`}
                    />
                    <Badge
                      variant={capability.isActive ? "success" : "default"}
                      className="rdm-status-badge"
                    >
                      {capability.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </h6>
                  <div className="rdm-field-grid">
                    <div className="rdm-field rdm-field--full">
                      <div className="rdm-field-label">Description</div>
                      <Textarea
                        rows={2}
                        className="min-h-[60px] text-sm"
                        value={capability.capabilityDescription || ""}
                        onChange={(e) =>
                          updateDraftCapability(
                            capIndex,
                            "capabilityDescription",
                            e.target.value
                          )
                        }
                        disabled={createGeneratedLoading}
                        placeholder="Capability description"
                      />
                    </div>
                  </div>

                  <h6 className="rdm-group-title mt-3">
                    Sub-capabilities ({capability.subCapabilities?.length || 0})
                  </h6>
                  {(capability.subCapabilities || []).length === 0 ? (
                    <div className="rdm-field-grid mt-2">
                      <div className="rdm-field rdm-field--full">
                        <div className="rdm-field-value mb-2">
                          No sub-capabilities. Questions, comment placeholders, and maturity belong to this capability.
                        </div>
                      </div>
                      <div className="rdm-field rdm-field--full">
                        <SubCapabilityQuestionListEditor
                          label="Current State Questions"
                          questions={capability.currentStateQuestions || []}
                          stateKind={QUESTION_STATE.current}
                          disabled={createGeneratedLoading}
                          placeholder="Current state questions"
                          onChange={(next) =>
                            updateDraftCapabilityQuestionList(
                              capIndex,
                              QUESTION_STATE.current,
                              next
                            )
                          }
                        />
                      </div>
                      <div className="rdm-field rdm-field--full">
                        <SubCapabilityQuestionListEditor
                          label="Future State Questions"
                          questions={capability.futureStateQuestions || []}
                          stateKind={QUESTION_STATE.future}
                          disabled={createGeneratedLoading}
                          placeholder="Future state questions"
                          onChange={(next) =>
                            updateDraftCapabilityQuestionList(
                              capIndex,
                              QUESTION_STATE.future,
                              next
                            )
                          }
                        />
                      </div>
                      <div className="rdm-field">
                        <div className="rdm-field-label">Current Comment Placeholder</div>
                        <Textarea
                          rows={2}
                          className="min-h-[60px] text-sm"
                          value={capability.currentStateCommentPlaceholder || ""}
                          onChange={(e) =>
                            updateDraftCapability(
                              capIndex,
                              "currentStateCommentPlaceholder",
                              e.target.value
                            )
                          }
                          disabled={createGeneratedLoading}
                        />
                      </div>
                      <div className="rdm-field">
                        <div className="rdm-field-label">Future Comment Placeholder</div>
                        <Textarea
                          rows={2}
                          className="min-h-[60px] text-sm"
                          value={capability.futureStateCommentPlaceholder || ""}
                          onChange={(e) =>
                            updateDraftCapability(
                              capIndex,
                              "futureStateCommentPlaceholder",
                              e.target.value
                            )
                          }
                          disabled={createGeneratedLoading}
                        />
                      </div>
                      <div className="rdm-field rdm-field--full">
                        <div className="rdm-maturity-block mt-1">
                          <div className="rdm-field-label mb-2">Maturity Scale</div>
                          <div className="rdm-maturity-list">
                            {(
                              parseMaturityOptionsJson(capability.maturityOptionsJson) ||
                              DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityOptions
                            ).map((option, optionIndex) => (
                              <div
                                key={`${option.optionValue ?? optionIndex}-${option.label}`}
                                className="rdm-maturity-item"
                              >
                                <div className="rdm-maturity-item__header">
                                  <span className="rdm-maturity-item__value">
                                    {option.optionValue ?? optionIndex + 1}
                                  </span>
                                  <span className="rdm-maturity-item__label">
                                    {option.label || "—"}
                                  </span>
                                </div>
                                <div className="rdm-maturity-item__definition">
                                  {option.definition || "—"}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    (capability.subCapabilities || []).map((sub, index) => {
                      const maturityOptions = Array.isArray(sub.maturityOptions)
                        ? sub.maturityOptions
                        : parseMaturityOptionsJson(sub.maturityOptionsJson) ||
                          DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityOptions;

                      return (
                        <div
                          key={sub.subId || index}
                          className="rdm-nested-card rdm-subcap-card"
                        >
                          <div className="rdm-nested-card-title d-flex align-items-center gap-2 flex-wrap">
                            <span className="rdm-subcap-index">{index + 1}</span>
                            <Input
                              className="rdm-edit-title h-8 text-sm"
                              value={sub.subCapabilityName || ""}
                              onChange={(e) =>
                                updateDraftSubCapability(
                                  capIndex,
                                  index,
                                  "subCapabilityName",
                                  e.target.value
                                )
                              }
                              disabled={createGeneratedLoading}
                              placeholder={`Sub-Capability ${index + 1}`}
                            />
                            <Badge
                              variant={sub.isActive !== false ? "success" : "default"}
                              className="rdm-status-badge"
                            >
                              {sub.isActive !== false ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <div className="rdm-field-grid mt-2">
                            <div className="rdm-field rdm-field--full">
                              <div className="rdm-field-label">Description</div>
                              <Textarea
                                rows={2}
                                className="min-h-[60px] text-sm"
                                value={sub.subCapabilityDescription || ""}
                                onChange={(e) =>
                                  updateDraftSubCapability(
                                    capIndex,
                                    index,
                                    "subCapabilityDescription",
                                    e.target.value
                                  )
                                }
                                disabled={createGeneratedLoading}
                                placeholder="Sub-capability description"
                              />
                            </div>
                            <div className="rdm-field rdm-field--full">
                              <SubCapabilityQuestionListEditor
                                label="Current State Questions"
                                questions={sub.currentStateQuestions || []}
                                stateKind={QUESTION_STATE.current}
                                disabled={createGeneratedLoading}
                                placeholder="Current state questions"
                                onChange={(next) =>
                                  updateDraftQuestionList(
                                    capIndex,
                                    index,
                                    QUESTION_STATE.current,
                                    next
                                  )
                                }
                              />
                            </div>
                            <div className="rdm-field rdm-field--full">
                              <SubCapabilityQuestionListEditor
                                label="Future State Questions"
                                questions={sub.futureStateQuestions || []}
                                stateKind={QUESTION_STATE.future}
                                disabled={createGeneratedLoading}
                                placeholder="Future state questions"
                                onChange={(next) =>
                                  updateDraftQuestionList(
                                    capIndex,
                                    index,
                                    QUESTION_STATE.future,
                                    next
                                  )
                                }
                              />
                            </div>
                            <div className="rdm-field">
                              <div className="rdm-field-label">Current Comment Placeholder</div>
                              <Textarea
                                rows={2}
                                className="min-h-[60px] text-sm"
                                value={sub.currentStateCommentPlaceholder || ""}
                                onChange={(e) =>
                                  updateDraftSubCapability(
                                    capIndex,
                                    index,
                                    "currentStateCommentPlaceholder",
                                    e.target.value
                                  )
                                }
                                disabled={createGeneratedLoading}
                              />
                            </div>
                            <div className="rdm-field">
                              <div className="rdm-field-label">Future Comment Placeholder</div>
                              <Textarea
                                rows={2}
                                className="min-h-[60px] text-sm"
                                value={sub.futureStateCommentPlaceholder || ""}
                                onChange={(e) =>
                                  updateDraftSubCapability(
                                    capIndex,
                                    index,
                                    "futureStateCommentPlaceholder",
                                    e.target.value
                                  )
                                }
                                disabled={createGeneratedLoading}
                              />
                            </div>
                          </div>
                          <div className="rdm-maturity-block mt-3">
                            <div className="rdm-field-label mb-2">Maturity Scale</div>
                            <div className="rdm-maturity-list">
                              {(maturityOptions || []).map((option, optionIndex) => (
                                <div
                                  key={`${option.optionValue ?? optionIndex}-${option.label}`}
                                  className="rdm-maturity-item"
                                >
                                  <div className="rdm-maturity-item__header">
                                    <span className="rdm-maturity-item__value">
                                      {option.optionValue ?? optionIndex + 1}
                                    </span>
                                    <span className="rdm-maturity-item__label">
                                      {option.label || "—"}
                                    </span>
                                  </div>
                                  <div className="rdm-maturity-item__definition">
                                    {option.definition || "—"}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </>
          ) : null}
        </div>
      </Modal>
    </DashboardShell>
  );
};

export default FrameworkManagement;
